import { Device } from '@capacitor/device';
import { CapacitorHttp } from '@capacitor/core';

// Conditional import for SplashScreen to prevent web build errors
let SplashScreen;
try {
  SplashScreen = require('@capacitor/splash-screen').SplashScreen;
} catch (error) {
  // SplashScreen not available on web builds
  SplashScreen = null;
}

// Track biometric authentication state at module level to persist across component re-mounts
let biometricAuthExecuted = false;
let biometricAuthShown = false;
let biometricAuthInProgress = false;

const DEBUG = process.env.NODE_ENV !== 'production';
const log = (...args) => { if (DEBUG) console.log(...args); };
const warn = (...args) => { if (DEBUG) console.warn(...args); };
const errorLog = (...args) => { if (DEBUG) console.error(...args); };

let Preferences;
const loadPreferences = async () => {
  if (!Preferences) {
    try {
      const moduleName = '@capacitor' + '/preferences';
      const pref = await import(
        /* webpackIgnore: true */
        moduleName
      );
      Preferences = pref.Preferences;
    } catch (e) {
      Preferences = null;
      warn('[NativeApiUtils] Preferences plugin not available', e);
    }
  }
};

// LocalStorage keys for persistent state
const BIOMETRIC_SESSION_KEY = 'sol_biometric_session_completed';
const BIOMETRIC_TIMESTAMP_KEY = 'sol_biometric_last_timestamp';

// Session timeout in milliseconds (30 minutes)
const BIOMETRIC_SESSION_TIMEOUT = 30 * 60 * 1000;

/**
 * Utility functions for making API calls that work on both web and native platforms
 * Can be used in both React components (via hook) and plain JavaScript modules
 */

// Check if running on native platform
export const checkNativePlatform = async () => {
  try {
    // Check if this is a mobile build first to avoid API lag
    if (process.env.REACT_APP_IS_MOBILE_BUILD === 'true') {
      log('[NativeApiUtils] Mobile build detected, mocking native platform');
      return true;
    }
    
    const deviceInfo = await Device.getInfo();
    const isNative = deviceInfo.platform === 'ios' || deviceInfo.platform === 'android';
    log(`[NativeApiUtils] Platform detection: ${deviceInfo.platform} (native: ${isNative})`);
    return isNative;
  } catch (error) {
    log('[NativeApiUtils] Device detection failed, assuming web platform');
    return false;
  }
};

// Splash screen helpers for native platforms
export const hideNativeSplash = async () => {
  try {
    const isNative = await checkNativePlatform();
    if (isNative && SplashScreen) {
      await SplashScreen.hide();
    }
  } catch (_) {
    // ignore
  }
};

// Sets up a fallback timeout to hide the splash screen on native devices
// Returns a cleanup function suitable for React useEffect
export const setupSplashFallbackHide = (timeoutMs = 8000) => {
  let canceled = false;
  let fallbackId;

  (async () => {
    try {
      const isNative = await checkNativePlatform();
      if (isNative && SplashScreen) {
        fallbackId = setTimeout(async () => {
          if (!canceled) {
            try { await SplashScreen.hide(); } catch (_) {}
          }
        }, timeoutMs);
      }
    } catch (_) {
      // ignore
    }
  })();

  return () => {
    canceled = true;
    if (fallbackId) clearTimeout(fallbackId);
  };
};

/**
 * Check if biometric authentication has already been executed
 * Checks both in-memory flag and localStorage for persistence
 * @returns {Promise<boolean>} True if biometric auth has been executed
 */
export const hasBiometricAuthExecuted = async () => {
  // Check in-memory flag first
  if (biometricAuthExecuted) {
    log('[NativeApiUtils] Biometric auth already executed (in-memory flag)');
    return true;
  }

  let isNative = false;
  try {
    isNative = await checkNativePlatform();
  } catch (_) {
    isNative = false;
  }

  let sessionCompleted;
  let lastTimestamp;
  if (isNative) {
    try {
      await loadPreferences();
      const session = await Preferences.get({ key: BIOMETRIC_SESSION_KEY });
      const ts = await Preferences.get({ key: BIOMETRIC_TIMESTAMP_KEY });
      sessionCompleted = session.value;
      lastTimestamp = ts.value;
    } catch (e) {
      warn('[NativeApiUtils] Failed to read biometric state from Preferences', e);
    }
  } else if (typeof localStorage !== 'undefined') {
    sessionCompleted = localStorage.getItem(BIOMETRIC_SESSION_KEY);
    lastTimestamp = localStorage.getItem(BIOMETRIC_TIMESTAMP_KEY);
  }
  
  if (sessionCompleted === 'true' && lastTimestamp) {
    const timeSinceAuth = Date.now() - parseInt(lastTimestamp, 10);
    const minutesAgo = Math.floor(timeSinceAuth / 60000);

    log(`[NativeApiUtils] Found biometric session in storage (${minutesAgo} minutes ago)`);
    
    // If within session timeout, consider it executed
    if (timeSinceAuth < BIOMETRIC_SESSION_TIMEOUT) {
      biometricAuthExecuted = true;
      log('[NativeApiUtils] Biometric session still valid, skipping auth');
      return true;
    } else {
      // Session expired, clear the flags
      log('[NativeApiUtils] Biometric session expired, clearing flags');
      if (isNative) {
        try {
          await loadPreferences();
          await Preferences.remove({ key: BIOMETRIC_SESSION_KEY });
          await Preferences.remove({ key: BIOMETRIC_TIMESTAMP_KEY });
        } catch (e) {
          warn('[NativeApiUtils] Failed to clear biometric state in Preferences', e);
        }
      } else if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(BIOMETRIC_SESSION_KEY);
        localStorage.removeItem(BIOMETRIC_TIMESTAMP_KEY);
      }
    }
  }

  log('[NativeApiUtils] No valid biometric session found');
  return false;
};

/**
 * Check if biometric authentication prompt has been shown
 * @returns {boolean} True if biometric prompt has been shown
 */
export const hasBiometricAuthShown = () => {
  return biometricAuthShown;
};

/**
 * Reset biometric authentication state (useful for testing or logout)
 */
export const resetBiometricAuthState = () => {
  biometricAuthExecuted = false;
  biometricAuthShown = false;

  (async () => {
    let isNative = false;
    try {
      isNative = await checkNativePlatform();
    } catch (_) {
      isNative = false;
    }

    if (isNative) {
      try {
        await loadPreferences();
        await Preferences.remove({ key: BIOMETRIC_SESSION_KEY });
        await Preferences.remove({ key: BIOMETRIC_TIMESTAMP_KEY });
      } catch (e) {
        warn('[NativeApiUtils] Failed to reset biometric state in Preferences', e);
      }
    } else if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(BIOMETRIC_SESSION_KEY);
      localStorage.removeItem(BIOMETRIC_TIMESTAMP_KEY);
    }
  })().catch((e) => warn('[NativeApiUtils] Failed to reset biometric state', e));

  log('[NativeApiUtils] Biometric auth state reset');
};

/**
 * Execute biometric authentication with built-in duplicate prevention
 * @param {Object} options - Authentication options
 * @param {number} options.delayMs - Delay before showing biometric prompt (default: 1500ms)
 * @param {string} options.reason - Reason for authentication
 * @param {boolean} options.exitOnCancel - Whether to exit app on cancel (default: true)
 * @returns {Promise<boolean>} True if authenticated successfully, false otherwise
 */
export const executeBiometricAuth = async (options = {}) => {
  const {
    delayMs = 1500,
    reason = 'Unlock Sol',
    exitOnCancel = true
  } = options;

  // Check if already executed using the helper function (checks both memory and localStorage)
  if (await hasBiometricAuthExecuted()) {
    log('[NativeApiUtils] Biometric auth already executed (from cache or storage), skipping');
    return true; // Return true as auth was previously successful
  }

  if (biometricAuthInProgress) {
    warn('[NativeApiUtils] Biometric auth already in progress, skipping duplicate call');
    return false;
  }
  biometricAuthInProgress = true;
  
  try {
    // Check if we're on native platform
    const isNative = await checkNativePlatform();
    if (!isNative) {
      log('[NativeApiUtils] Not on native platform, skipping biometric auth');
      biometricAuthExecuted = true;
      return true;
    }

    biometricAuthExecuted = true;

    // Add delay to ensure splash screen is fully displayed before showing biometric
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // Mark biometric as shown
    biometricAuthShown = true;

    // Dynamically import the plugin only on native to avoid web bundling issues
    let BiometricAuth;
    try {
      ({ BiometricAuth } = await import('@aparajita/capacitor-biometric-auth'));
    } catch (e) {
      warn('[NativeApiUtils] Biometric plugin not available', e);
      biometricAuthExecuted = false;
      biometricAuthShown = false;
      return false;
    }
    
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Use passcode',
      androidTitle: 'Biometric login',
      androidSubtitle: 'Unlock your session',
      androidConfirmationRequired: false
    });

    log('[NativeApiUtils] Biometric authentication succeeded');
    
    // Persist to storage to prevent duplicate prompts on refresh
    const timestamp = Date.now().toString();
    try {
      await loadPreferences();
      await Preferences.set({ key: BIOMETRIC_SESSION_KEY, value: 'true' });
      await Preferences.set({ key: BIOMETRIC_TIMESTAMP_KEY, value: timestamp });
      log('[NativeApiUtils] Biometric session saved to Preferences');
    } catch (e) {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(BIOMETRIC_SESSION_KEY, 'true');
        localStorage.setItem(BIOMETRIC_TIMESTAMP_KEY, timestamp);
        log('[NativeApiUtils] Biometric session saved to localStorage');
      } else {
        warn('[NativeApiUtils] Failed to persist biometric session', e);
      }
    }
    
    return true;

  } catch (authError) {
    // Authentication failed or was canceled
    warn('[NativeApiUtils] Biometric authentication failed or canceled:', authError);
    
    // Reset the execution flag since auth failed
    biometricAuthExecuted = false;
    biometricAuthShown = false;

    // Clear persisted flags
    try {
      await loadPreferences();
      await Preferences.remove({ key: BIOMETRIC_SESSION_KEY });
      await Preferences.remove({ key: BIOMETRIC_TIMESTAMP_KEY });
    } catch (e) {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(BIOMETRIC_SESSION_KEY);
        localStorage.removeItem(BIOMETRIC_TIMESTAMP_KEY);
      }
    }

    if (exitOnCancel) {
      // Clear stored auth data before exiting
      try {
        const { clearAuthData } = await import('./sessionStorage');
        clearAuthData();
        log('[NativeApiUtils] Cleared auth data before exit');
      } catch (clearError) {
        errorLog('[NativeApiUtils] Failed to clear auth data:', clearError);
      }

      // Close the app
      try {
        const { App } = await import('@capacitor/app');
        log('[NativeApiUtils] Exiting app due to biometric auth failure');
        await App.exitApp();
      } catch (exitError) {
        errorLog('[NativeApiUtils] Failed to exit app:', exitError);
      }
    }

    return false;
  } finally {
    biometricAuthInProgress = false;
  }
};

// Unified API call function that works on both web and native
export const makeApiCall = async (url, options = {}) => {
  const isNative = await checkNativePlatform();
  
  if (isNative) {
    // log(`[NativeApiUtils] Making native API call to: ${url}`);
    
    // Use Capacitor HTTP for native platforms
    const capacitorOptions = {
      url: url,
      method: options.method || 'GET',
      headers: options.headers || {},
    };
    
    if (options.body) {
      if (options.body instanceof FormData) {
        // Handle FormData for file uploads
        capacitorOptions.data = options.body;
      } else if (typeof options.body === 'string') {
        try {
          capacitorOptions.data = JSON.parse(options.body);
        } catch {
          capacitorOptions.data = options.body;
        }
      } else {
        capacitorOptions.data = options.body;
      }
    }
    
    try {
      const response = await CapacitorHttp.request(capacitorOptions);

      // Create a headers object that mimics the Headers API
      const headersObj = response.headers || {};
      const headers = {
        get: (name) => {
          const lowerName = name.toLowerCase();
          const key = Object.keys(headersObj).find(k => k.toLowerCase() === lowerName);
          return key ? headersObj[key] : null;
        },
        has: (name) => {
          const lowerName = name.toLowerCase();
          return Object.keys(headersObj).some(k => k.toLowerCase() === lowerName);
        }
      };
      
      // CapacitorHttp automatically parses JSON responses, so response.data is already an object
      // We need to handle this properly for the frontend code that expects to call .json()
      const isJsonResponse = typeof response.data === 'object' && response.data !== null;
      
      // Convert Capacitor response to fetch-like response
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        statusText: response.status >= 200 && response.status < 300 ? 'OK' : 'Error',
        headers: headers,
        json: async () => {
          // If data is already parsed (object), return it directly
          if (isJsonResponse) {
            return response.data;
          }
          // If it's a string, try to parse it
          if (typeof response.data === 'string') {
            return JSON.parse(response.data);
          }
          return response.data;
        },
        text: async () => typeof response.data === 'string' ? response.data : JSON.stringify(response.data)
      };
    } catch (error) {
      errorLog('[NativeApiUtils] Capacitor HTTP error:', error);
      throw error;
    }
  } else {
    // log(`[NativeApiUtils] Making web API call to: ${url}`);
    
    // Use standard fetch for web platforms
    const response = await fetch(url, options);
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      json: async () => await response.json(),
      text: async () => await response.text()
    };
  }
};
